/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-unsafe-argument */
import React, { useRef, useCallback, useState, useContext, useEffect } from "react"
import Swiper, { SwiperRefNode } from "react-id-swiper"
import { useSearchParams } from "react-router-dom"

import SwiperType from "swiper"

import "swiper/swiper.scss"

import { SwiperWrapper } from "./styles"

const IS_DEV = process?.env?.NODE_ENV === "development"

export type SliderContextProps = {
  goNext: (params?: any) => void
  goPrev: (params?: any) => void
  goTo: (index: number, params?: any, opt?: { animate?: boolean }) => void
  index: number
  maxIndex: number
  params: any
  swiperWrapperRef: React.RefObject<HTMLDivElement> | null
  swiperRef: React.RefObject<HTMLDivElement> | null
}

const SliderContext = React.createContext<SliderContextProps>({
  goNext: (_params?: any) => undefined,
  goPrev: (_params?: any) => undefined,
  goTo: (_index: number, _params?: any, _opt?: { animate?: boolean }) => undefined,
  index: 0,
  maxIndex: 0,
  params: {} as any,
  swiperWrapperRef: null,
  swiperRef: null,
})

export const useSlider = () => {
  const props = useContext(SliderContext)
  return props
}

enum DirectionType {
  NEXT = "NEXT",
  PREV = "PREV",
}

type Props = {
  children: JSX.Element[]
  shouldFocuseFirstElement?: boolean
  onSlide?: () => void
  header?: (props: SliderContextProps) => void
  footer?: (props: SliderContextProps) => void
}

type ProviderProps = {
  children: JSX.Element
  maxSlides: number
  shouldFocuseFirstElement?: boolean
  onSlide?: () => void
}

type ConsumerProps = {
  children: JSX.Element[]
  header?: (props: SliderContextProps) => void
  footer?: (props: SliderContextProps) => void
}

export const SliderConsumer = ({
  children = [],
  header = () => undefined,
  footer = () => undefined,
}: ConsumerProps) => {
  const props = useSlider()

  return (
    <SwiperWrapper
      ref={props.swiperWrapperRef}
      id="slider-wrapper"
      className={`active-slide-${props.index}`}
    >
      {header(props)}
      <Swiper ref={props.swiperRef} noSwiping spaceBetween={24}>
        {children}
      </Swiper>
      {footer(props)}
    </SwiperWrapper>
  )
}

export const focusFirstElement = (container: Element, prioritizeClass?: string) => {
  const elemntsTofocus = "select, input, textarea, button, a, [role='listbox']"

  const eleToFocus = prioritizeClass
    ? container?.querySelector(prioritizeClass)
    : undefined
  const all = container?.querySelectorAll(elemntsTofocus)

  if (eleToFocus) {
    const firstElement = eleToFocus as HTMLInputElement
    firstElement.focus()
  } else if (all?.[0]) {
    const firstElement = all[0] as HTMLInputElement
    firstElement.focus()
  }
}

export const SliderProvider: React.FC<ProviderProps> = ({
  maxSlides,
  shouldFocuseFirstElement,
  children,
  onSlide,
}) => {
  const swiperRef = useRef<HTMLDivElement>(null)
  const swiperWrapperRef = useRef<HTMLDivElement>(null)
  const [params, setParams] = useState({})
  const [slideIndex, setSlideIndex] = useState(0)
  // eslint-disable-next-line no-sparse-arrays, react-hooks/rules-of-hooks
  const [search, setSearch] = IS_DEV ? useSearchParams() : [{ get: () => {} }, () => {}]

  let sliding = false

  const go = useCallback(
    (direction: DirectionType) =>
      (paramsArg: any = {}) => {
        const { current } = swiperRef
        const { swiper } = current as SwiperRefNode

        if (swiper && !sliding) {
          if (direction === DirectionType.NEXT) {
            swiper.slideNext()
          } else {
            swiper.slidePrev()
          }
          setParams(paramsArg)
          setSlideIndex(swiper.realIndex)
          if (IS_DEV) {
            setSearch({ slide: swiper.realIndex.toString() })
          }
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
      },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [swiperRef, search],
  )

  const goTo = useCallback(
    (index: number, paramsArg: any = {}, opt = { animate: true }) => {
      const { current } = swiperRef
      const { swiper } = current as SwiperRefNode

      if (swiper && !sliding) {
        swiper.slideTo(index, !opt?.animate ? 0 : undefined)
        setParams(paramsArg)
        setSlideIndex(swiper.realIndex)
        if (IS_DEV) {
          setSearch({ slide: swiper.realIndex.toString() })
        }
      }

      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [swiperRef, setSearch],
  )

  useEffect(() => {
    if (IS_DEV) {
      const urlSlideIndex = Number(search.get("slide"))
      if (urlSlideIndex !== slideIndex) {
        goTo(urlSlideIndex)
      }
    }
  }, [search, IS_DEV])

  const props = {
    goNext: go(DirectionType.NEXT),
    goPrev: go(DirectionType.PREV),
    goTo,
    index: slideIndex,
    maxIndex: maxSlides || 0,
    params,
    swiperWrapperRef,
    swiperRef,
  }

  useEffect(() => {
    const { current } = swiperRef
    const { swiper } = current as SwiperRefNode
    const { slides } = swiper as SwiperType

    swiper?.on("transitionStart", () => {
      sliding = true
      swiper.el.classList.add("swiping")
    })
    swiper?.on("transitionEnd", ({ realIndex }) => {
      sliding = false
      swiper.el.classList.remove("swiping")

      swiperWrapperRef?.current?.scrollTo(0, 0)
      if (shouldFocuseFirstElement) {
        focusFirstElement(slides[realIndex])
      }

      if (onSlide) {
        onSlide()
      }
    })

    // init for the first time
    if (shouldFocuseFirstElement) {
      focusFirstElement(slides[0])
    }
  }, [])

  return <SliderContext.Provider value={props}>{children}</SliderContext.Provider>
}

const Slider: React.FC<Props> = ({
  children = [],
  shouldFocuseFirstElement = false,
  header = () => undefined,
  footer = () => undefined,
  onSlide = () => undefined,
}) => {
  const maxIndex = children.length - 1
  return (
    <SliderProvider
      maxSlides={maxIndex}
      shouldFocuseFirstElement={shouldFocuseFirstElement}
      onSlide={onSlide}
    >
      <SliderConsumer footer={footer} header={header}>
        {children}
      </SliderConsumer>
    </SliderProvider>
  )
}

export default Slider
